// Chart customization state — shape, defaults, and localStorage persistence.
//
// Per-(view, group_by) keys: a user who sets "horizontal_bar" for the
// Country breakdown keeps that choice on the Country breakdown only, not
// on the Status breakdown. Stored under one umbrella JSON key so we can
// migrate the whole bag in one go if the shape changes.
import { RETIRED_FIELD_KEYS } from "../../lib/filterLogic";
import type { ChartType } from "../../types/chart";
import type { PaletteId } from "./palettes";

export type LabelMode = "count" | "percent" | "both" | "hide";
export type SortMode = "desc" | "asc" | "alpha";
/** Rotation (degrees) applied to the bar chart's category-axis labels. */
export type AxisLabelAngle = 0 | 45 | 90;

export interface ChartCustomization {
  chartType: ChartType;
  palette: PaletteId;
  labelMode: LabelMode;
  sortMode: SortMode;
  topN: number;
  showLegend: boolean;
  axisLabelAngle: AxisLabelAngle;
}

export const DEFAULT_CUSTOMIZATION: ChartCustomization = {
  chartType: "donut",
  palette: "innodia",
  labelMode: "both",
  sortMode: "desc",
  topN: 10,
  showLegend: true,
  axisLabelAngle: 0,
};

const STORAGE_KEY = "inpact:chart-customization";

type StoredBag = Record<string, Partial<ChartCustomization>>;

function readBag(): StoredBag {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? (parsed as StoredBag) : {};
  } catch {
    return {};
  }
}

function writeBag(bag: StoredBag): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bag));
  } catch {
    // Quota exceeded / private mode — fail silent, customization is a nice-to-have.
  }
}

function keyFor(view: string, groupBy: string): string {
  return `${view}::${groupBy}`;
}

export function loadCustomization(
  view: string,
  groupBy: string,
  fallback: Partial<ChartCustomization> = {},
): ChartCustomization {
  const bag = readBag();
  // Settings are keyed by the exact group-by string, so a field key that
  // changed leaves the user's saved chart layout stranded under the old
  // name — it would silently reset to defaults the first time they open the
  // chart after a deploy. When the CRM retires a field in favour of another
  // (the country text fields → their picklists), the settings move with it.
  const stored =
    bag[keyFor(view, groupBy)] ?? bag[keyFor(view, predecessorOf(groupBy))] ?? {};
  return { ...DEFAULT_CUSTOMIZATION, ...fallback, ...stored };
}

/** The key a retired field's settings would have been saved under. Mirrors
 *  `RETIRED_FIELD_KEYS` in `lib/filterLogic.ts`, read backwards: that map
 *  answers "what replaced this?", and here we need "what did this replace?". */
function predecessorOf(groupBy: string): string {
  for (const [retired, replacement] of Object.entries(RETIRED_FIELD_KEYS)) {
    if (replacement === groupBy) return retired;
  }
  return groupBy;
}

export function saveCustomization(
  view: string,
  groupBy: string,
  next: ChartCustomization,
): void {
  const bag = readBag();
  bag[keyFor(view, groupBy)] = next;
  writeBag(bag);
}
